import React from 'react';
import { StyleSheet, ActivityIndicator, ViewStyle, StyleProp } from 'react-native';
import { Text } from '@/components/common/text';
import { Hoverable } from '@/components/shared/redesign/Hoverable';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { PRIMITIVE_COLORS } from '@/constants/residents/residentColors';

export type AdminButtonVariant = 'primary' | 'secondary' | 'destructive' | 'outline';

interface ButtonProps {
  title: string;
  onPress: () => void;
  /**
   * - `primary`: solid Resio blue background, white text (both themes)
   * - `secondary`: transparent glass background with themed border + text
   * - `destructive`: transparent glass background with themed red text
   * - `outline`: legacy alias kept for back-compat — same look as `secondary`
   *   but with a tinted border. Prefer `secondary`.
   */
  variant?: AdminButtonVariant;
  disabled?: boolean;
  loading?: boolean;
  style?: StyleProp<ViewStyle>;
}

export const Button: React.FC<ButtonProps> = ({
  title,
  onPress,
  variant = 'primary',
  disabled = false,
  loading = false,
  style,
}) => {
  const styles = useStyles();
  const { admin } = useAdminColors();

  const containerStyleByVariant: Record<AdminButtonVariant, StyleProp<ViewStyle>> = {
    primary: [styles.button, styles.primaryButton],
    secondary: [styles.button, styles.secondaryButton],
    destructive: [styles.button, styles.destructiveButton],
    outline: [styles.button, styles.outlineButton],
  };

  const textStyleByVariant = {
    primary: [styles.buttonText, styles.primaryButtonText],
    secondary: [styles.buttonText, styles.secondaryButtonText],
    destructive: [styles.buttonText, styles.destructiveButtonText],
    outline: [styles.buttonText, styles.outlineButtonText],
  };

  const indicatorColor = variant === 'primary' ? '#FFFFFF' : admin.formInput.borderFocused;

  return (
    <Hoverable
      hover={variant === 'primary' || variant === 'destructive' ? 'button' : 'glass'}
      style={[containerStyleByVariant[variant], disabled && styles.disabledButton, style]}
      onPress={onPress}
      disabled={disabled || loading}
    >
      {loading ? (
        <ActivityIndicator size="small" color={indicatorColor} />
      ) : (
        <Text style={[textStyleByVariant[variant], disabled && styles.disabledButtonText]}>
          {title}
        </Text>
      )}
    </Hoverable>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, semantic }) => {
    const admin = components.admin;
    return StyleSheet.create({
      button: {
        paddingVertical: 12,
        paddingHorizontal: 24,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 48,
      },
      primaryButton: {
        backgroundColor: PRIMITIVE_COLORS.resioBlue,
      },
      secondaryButton: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: admin.dataTable.border,
      },
      destructiveButton: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: semantic.status.error,
      },
      outlineButton: {
        backgroundColor: 'transparent',
        borderWidth: 1,
        borderColor: admin.formInput.borderFocused,
      },
      disabledButton: {
        opacity: 0.6,
      },
      buttonText: {
        fontSize: 16,
        fontWeight: '500',
      },
      primaryButtonText: {
        color: '#FFFFFF',
      },
      secondaryButtonText: {
        color: admin.dataTable.text,
      },
      destructiveButtonText: {
        color: semantic.status.error,
      },
      outlineButtonText: {
        color: admin.formInput.borderFocused,
      },
      disabledButtonText: {
        opacity: 0.6,
      },
    });
  });
